/**
 * useGymProgress Hook
 *
 * Calculates the user's climbing progress at a gym:
 * - How many unique routes they've sent
 * - Total active routes at the gym
 * - Progress percentage
 */

import { useMemo } from 'react';
import { useRoutes } from './useRoutes';
import { useClimbLogs } from './useClimbLogs';

/**
 * Return type for useGymProgress hook
 */
export interface GymProgressResult {
  /** Number of unique routes the user has sent at this gym */
  sendsCount: number;
  /** Total number of active routes at the gym */
  totalRoutes: number;
  /** Progress as a percentage (0-100) */
  progressPercent: number;
  /** Whether data is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Hook to calculate user's progress at a gym
 *
 * @param gymId - The gym ID to calculate progress for
 * @returns Progress statistics for the gym
 *
 * @example
 * ```tsx
 * const { sendsCount, totalRoutes, progressPercent, loading } = useGymProgress(gymId);
 *
 * return (
 *   <div>
 *     <ProgressRing percent={progressPercent} />
 *     <span>{sendsCount}/{totalRoutes}</span>
 *   </div>
 * );
 * ```
 */
export const useGymProgress = (gymId: string | undefined): GymProgressResult => {
  // Get all active routes at the gym
  const {
    routes,
    loading: routesLoading,
    error: routesError,
  } = useRoutes(gymId, {
    initialFilters: { includeRetired: false },
  });

  // Get user's climb logs for this gym
  const {
    logs,
    loading: logsLoading,
    error: logsError,
  } = useClimbLogs({
    initialFilters: { gymId },
  });

  // Calculate unique routes the user has sent
  const sendsCount = useMemo(() => {
    if (!gymId || routes.length === 0) return 0;

    // Get set of route IDs that exist at this gym
    const activeRouteIds = new Set(routes.map((r) => r.id));

    // Get unique route IDs from successful sends
    const sentRouteIds = new Set(
      logs
        .filter((log) => log.outcome === 'send' && activeRouteIds.has(log.routeId))
        .map((log) => log.routeId)
    );

    return sentRouteIds.size;
  }, [gymId, routes, logs]);

  const totalRoutes = routes.length;

  const progressPercent = useMemo(() => {
    if (totalRoutes === 0) return 0;
    return (sendsCount / totalRoutes) * 100;
  }, [sendsCount, totalRoutes]);

  return {
    sendsCount,
    totalRoutes,
    progressPercent,
    loading: routesLoading || logsLoading,
    error: routesError ?? logsError,
  };
};

export default useGymProgress;
